import JSZip from 'jszip'
import pdfjsLib from '@/utils/pdfjs'

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp']
const EBOOK_EXTENSIONS = ['epub', 'mobi', 'azw', 'azw3']
const IMAGE_PAGE_NUMBER = 1
const UNKNOWN_PAGE_NUMBER = 0

function getFileExtension(fileName) {
  const value = String(fileName || '').trim().toLowerCase()
  const dotIndex = value.lastIndexOf('.')
  return dotIndex >= 0 && dotIndex < value.length - 1 ? value.slice(dotIndex + 1) : ''
}

function normalizePageNumber(value) {
  const number = Math.floor(Number(value) || 0)
  return number > 0 ? number : UNKNOWN_PAGE_NUMBER
}

function readFileAsArrayBuffer(file) {
  if (file && typeof file.arrayBuffer == 'function') {
    return file.arrayBuffer()
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = event => resolve(event && event.target ? event.target.result : null)
    reader.onerror = () => reject(reader.error || new Error('File read failed'))
    reader.readAsArrayBuffer(file)
  })
}

function decodeXmlEntities(value) {
  return String(value || '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#39;/g, "'")
}

function parseXmlAttributes(tag) {
  const attrs = {}
  String(tag || '').replace(/([:\w-]+)\s*=\s*(["'])(.*?)\2/g, (match, name, quote, value) => {
    attrs[String(name || '').toLowerCase()] = decodeXmlEntities(value)
    return match
  })
  return attrs
}

function normalizeZipPath(path) {
  return String(path || '').replace(/\\/g, '/').replace(/^\/+/, '')
}

function joinZipPath(basePath, href) {
  const rawHref = String(href || '').split('#')[0].split('?')[0]
  const isAbsolute = /^[\\/]+/.test(rawHref)
  const cleanHref = normalizeZipPath(rawHref)
  if (!cleanHref) {
    return ''
  }
  const normalizedBase = normalizeZipPath(basePath)
  const baseDir = normalizedBase.includes('/') ? normalizedBase.slice(0, normalizedBase.lastIndexOf('/')) : ''
  const rawPath = isAbsolute ? cleanHref : [baseDir, cleanHref].filter(Boolean).join('/')
  const parts = []
  rawPath.split('/').forEach(part => {
    if (!part || part == '.') {
      return
    }
    if (part == '..') {
      parts.pop()
      return
    }
    parts.push(part)
  })
  return parts.join('/')
}

function getZipFile(zip, path) {
  const normalized = normalizeZipPath(path)
  if (!normalized) {
    return null
  }
  if (zip.files[normalized]) {
    return zip.files[normalized]
  }
  const lower = normalized.toLowerCase()
  const name = Object.keys(zip.files).find(item => normalizeZipPath(item).toLowerCase() == lower)
  return name ? zip.files[name] : null
}

async function readZipFileText(zip, path) {
  const file = getZipFile(zip, path)
  return file && !file.dir ? await file.async('string') : ''
}

function findZipPath(zip, pattern) {
  const names = Object.keys(zip && zip.files ? zip.files : {})
  const name = names.find(item => {
    const file = zip.files[item]
    return file && !file.dir && pattern.test(normalizeZipPath(item))
  })
  return name || ''
}

function extractOpfPath(containerText) {
  const match = String(containerText || '').match(/<rootfile\b[^>]*\bfull-path\s*=\s*(["'])(.*?)\1[^>]*>/i)
  return match ? decodeXmlEntities(match[2]) : ''
}

function parseOpfManifest(opfText, opfPath) {
  const items = []
  String(opfText || '').replace(/<item\b[^>]*>/gi, tag => {
    const attrs = parseXmlAttributes(tag)
    if (attrs.id && attrs.href) {
      items.push({
        id: attrs.id,
        href: attrs.href,
        path: joinZipPath(opfPath, attrs.href),
        mediaType: attrs['media-type'] || '',
        properties: attrs.properties || ''
      })
    }
    return tag
  })
  return items
}

function parseOpfSpineIds(opfText) {
  const ids = []
  String(opfText || '').replace(/<itemref\b[^>]*>/gi, tag => {
    const attrs = parseXmlAttributes(tag)
    if (attrs.idref && String(attrs.linear || 'yes').toLowerCase() != 'no') {
      ids.push(attrs.idref)
    }
    return tag
  })
  return ids
}

function extractExplicitPageCount(text) {
  const source = String(text || '')
  const patterns = [
    /<meta\b[^>]*(?:name|property)\s*=\s*["'][^"']*(?:page[-_ ]?count|number[-_ ]?of[-_ ]?pages|numberofpages|numberOfPages|total[-_ ]?pages|num[-_ ]?pages|pages)[^"']*["'][^>]*\bcontent\s*=\s*["'](\d+)["'][^>]*>/i,
    /<meta\b[^>]*\bcontent\s*=\s*["'](\d+)["'][^>]*(?:name|property)\s*=\s*["'][^"']*(?:page[-_ ]?count|number[-_ ]?of[-_ ]?pages|numberofpages|numberOfPages|total[-_ ]?pages|num[-_ ]?pages|pages)[^"']*["'][^>]*>/i,
    /<meta\b[^>]*\bproperty\s*=\s*["'][^"']*(?:schema:numberOfPages|prism:pageCount)[^"']*["'][^>]*>\s*(\d+)\s*<\/meta>/i,
    /["'](?:pageCount|page_count|pageNumber|page_number|totalPage|totalPages|numPages|numberOfPages)["']\s*:\s*(\d+)/i,
    /\b(?:pageCount|page_count|pageNumber|page_number|totalPage|totalPages|numPages|numberOfPages)\b\s*[:=]\s*["']?(\d+)["']?/i
  ]
  for (let i = 0; i < patterns.length; i++) {
    const match = source.match(patterns[i])
    const count = match ? normalizePageNumber(match[1]) : 0
    if (count > UNKNOWN_PAGE_NUMBER) {
      return count
    }
  }
  return 0
}

function countElementMatches(text, pattern) {
  const matches = String(text || '').match(pattern)
  return matches ? matches.length : 0
}

function countPageListEntries(text) {
  const source = String(text || '')
  const navSections = source.match(/<nav\b[\s\S]*?<\/nav>/gi) || []
  for (let i = 0; i < navSections.length; i++) {
    const section = navSections[i]
    if (!/(?:epub:type|type|role)\s*=\s*["'][^"']*(?:page-list|doc-pagelist)[^"']*["']/i.test(section)) {
      continue
    }
    const count = countElementMatches(section, /<li\b[^>]*>/gi)
      || countElementMatches(section, /<a\b[^>]*>/gi)
      || countElementMatches(section, /<span\b[^>]*>/gi)
    if (count > 0) {
      return count
    }
  }

  const pageListSections = source.match(/<pageList\b[\s\S]*?<\/pageList>/gi) || []
  for (let i = 0; i < pageListSections.length; i++) {
    const count = countElementMatches(pageListSections[i], /<pageTarget\b[^>]*>/gi)
    if (count > 0) {
      return count
    }
  }
  return 0
}

function hasPageBreakTag(tag) {
  const text = String(tag || '')
  return /<\s*mbp:pagebreak\b/i.test(text)
    || /\b(?:epub:type|role|class)\s*=\s*["'][^"']*(?:pagebreak|doc-pagebreak)[^"']*["']/i.test(text)
}

function countPageBreakTags(text, minimumEndIndex = 0) {
  const tags = String(text || '').match(/<[^>]+>/g) || []
  let count = 0
  let cursor = 0
  for (let i = 0; i < tags.length; i++) {
    const index = String(text || '').indexOf(tags[i], cursor)
    const endIndex = index >= 0 ? index + tags[i].length : cursor + tags[i].length
    cursor = endIndex
    if (endIndex > minimumEndIndex && hasPageBreakTag(tags[i])) {
      count += 1
    }
  }
  return count
}

function isEpubFixedLayout(opfText) {
  const source = String(opfText || '')
  return /<meta\b[^>]*\bproperty\s*=\s*["']rendition:layout["'][^>]*>\s*pre-paginated\s*<\/meta>/i.test(source)
    || /<meta\b[^>]*\bname\s*=\s*["']rendition:layout["'][^>]*\bcontent\s*=\s*["']pre-paginated["'][^>]*>/i.test(source)
    || /<meta\b[^>]*\bname\s*=\s*["']fixed-layout["'][^>]*\bcontent\s*=\s*["'](?:true|yes)["'][^>]*>/i.test(source)
}

function getEpubTextManifestItems(manifest) {
  return manifest.filter(item => {
    const mediaType = String(item.mediaType || '')
    const path = String(item.path || '')
    return /(?:application\/xhtml\+xml|text\/html|application\/x-dtbncx\+xml|application\/xml|text\/xml)/i.test(mediaType)
      || /\.(?:xhtml|html|htm|xml|ncx)$/i.test(path)
  })
}

function getEpubZipTextPaths(zip) {
  return Object.keys(zip && zip.files ? zip.files : {})
    .filter(path => {
      const file = zip.files[path]
      return file && !file.dir && /\.(?:xhtml|html|htm|xml|ncx)$/i.test(normalizeZipPath(path))
    })
}

async function findEpubPageListCount(zip, items) {
  const seen = {}
  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    const path = item && item.path ? item.path : item
    const normalized = normalizeZipPath(path).toLowerCase()
    if (!normalized || seen[normalized]) {
      continue
    }
    seen[normalized] = true
    const count = countPageListEntries(await readZipFileText(zip, path))
    if (count > 0) {
      return count
    }
  }
  return 0
}

async function countEpubPagesFromBuffer(buffer) {
  const zip = await JSZip.loadAsync(buffer)
  const containerText = await readZipFileText(zip, 'META-INF/container.xml')
  const opfPath = extractOpfPath(containerText) || findZipPath(zip, /\.opf$/i)
  const opfText = opfPath ? await readZipFileText(zip, opfPath) : ''
  const explicitCount = extractExplicitPageCount(opfText)
  if (explicitCount > 0) {
    return explicitCount
  }

  const manifest = parseOpfManifest(opfText, opfPath)
  const navItem = manifest.find(item => /\bnav\b/i.test(item.properties))
  const ncxItem = manifest.find(item => /application\/x-dtbncx\+xml/i.test(item.mediaType))
  const textItems = getEpubTextManifestItems(manifest)
  const zipTextPaths = getEpubZipTextPaths(zip)
  const pageListCount = await findEpubPageListCount(zip, [navItem, ncxItem].filter(Boolean).concat(textItems, zipTextPaths))
  if (pageListCount > 0) {
    return pageListCount
  }

  const manifestById = {}
  manifest.forEach(item => {
    manifestById[item.id] = item
  })
  const spinePaths = parseOpfSpineIds(opfText)
    .map(id => manifestById[id])
    .filter(Boolean)
    .map(item => item.path)
  const contentPaths = spinePaths.length > 0
    ? spinePaths
    : manifest.filter(item => /(?:application\/xhtml\+xml|text\/html)/i.test(item.mediaType)).map(item => item.path)
  const fallbackContentPaths = contentPaths.length > 0 ? contentPaths : zipTextPaths
  let pageBreakCount = 0
  for (let i = 0; i < fallbackContentPaths.length; i++) {
    pageBreakCount += countPageBreakTags(await readZipFileText(zip, fallbackContentPaths[i]))
  }
  if (pageBreakCount > 0) {
    return pageBreakCount
  }
  return isEpubFixedLayout(opfText) && spinePaths.length > 0 ? spinePaths.length : UNKNOWN_PAGE_NUMBER
}

function readUInt16BE(bytes, offset) {
  if (!bytes || offset < 0 || offset + 2 > bytes.length) {
    return 0
  }
  return (bytes[offset] << 8) | bytes[offset + 1]
}

function readUInt32BE(bytes, offset) {
  if (!bytes || offset < 0 || offset + 4 > bytes.length) {
    return 0
  }
  return ((bytes[offset] * 0x1000000) + ((bytes[offset + 1] << 16) | (bytes[offset + 2] << 8) | bytes[offset + 3])) >>> 0
}

function decodeBytesToString(bytes) {
  if (!bytes || bytes.length == 0) {
    return ''
  }
  if (typeof TextDecoder != 'undefined') {
    return new TextDecoder('utf-8').decode(bytes)
  }
  let text = ''
  for (let i = 0; i < bytes.length; i++) {
    text += String.fromCharCode(bytes[i])
  }
  return text
}

function decodeAsciiBytes(bytes) {
  let text = ''
  const chunkSize = 8192
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length))
    let part = ''
    for (let j = 0; j < chunk.length; j++) {
      part += String.fromCharCode(chunk[j])
    }
    text += part
  }
  return text
}

function countPatternMatches(text, pattern, minimumEndIndex = 0) {
  let count = 0
  pattern.lastIndex = 0
  let match = pattern.exec(text)
  while (match) {
    if (match.index + match[0].length > minimumEndIndex) {
      count += 1
    }
    match = pattern.exec(text)
  }
  return count
}

function getMaxPdfPageTreeCount(text) {
  const source = String(text || '')
  const patterns = [
    /\/Type\s*\/Pages\b[\s\S]{0,2000}?\/Count\s+(\d+)/g,
    /\/Count\s+(\d+)[\s\S]{0,2000}?\/Type\s*\/Pages\b/g
  ]
  let maxCount = 0
  for (let i = 0; i < patterns.length; i++) {
    const pattern = patterns[i]
    pattern.lastIndex = 0
    let match = pattern.exec(source)
    while (match) {
      maxCount = Math.max(maxCount, normalizePageNumber(match[1]))
      match = pattern.exec(source)
    }
  }
  return maxCount
}

function countPdfPagesFromBytes(bytes) {
  if (!bytes || bytes.length == 0) {
    return UNKNOWN_PAGE_NUMBER
  }
  const chunkSize = 512 * 1024
  let maxPageTreeCount = 0
  let leafPageCount = 0
  let carry = ''
  for (let start = 0; start < bytes.length; start += chunkSize) {
    const chunk = bytes.subarray(start, Math.min(start + chunkSize, bytes.length))
    const text = carry + decodeAsciiBytes(chunk)
    maxPageTreeCount = Math.max(maxPageTreeCount, getMaxPdfPageTreeCount(text))
    leafPageCount += countPatternMatches(text, /\/Type\s*\/Page\b(?!s)/g, carry.length)
    carry = text.slice(-4096)
  }
  return maxPageTreeCount || leafPageCount || UNKNOWN_PAGE_NUMBER
}

function getMobiRecordRanges(bytes) {
  const recordCount = readUInt16BE(bytes, 76)
  if (!recordCount || 78 + recordCount * 8 > bytes.length) {
    return []
  }
  const offsets = []
  for (let i = 0; i < recordCount; i++) {
    const offset = readUInt32BE(bytes, 78 + i * 8)
    if (offset > 0 && offset < bytes.length) {
      offsets.push(offset)
    }
  }
  offsets.sort((a, b) => a - b)
  return offsets.map((offset, index) => ({
    start: offset,
    end: offsets[index + 1] || bytes.length
  })).filter(range => range.end > range.start)
}

function decompressPalmDocRecord(input) {
  const output = []
  for (let i = 0; i < input.length; i++) {
    const value = input[i]
    if (value >= 1 && value <= 8) {
      const end = Math.min(i + value, input.length - 1)
      while (i < end) {
        i += 1
        output.push(input[i])
      }
      continue
    }
    if (value <= 0x7f) {
      output.push(value)
      continue
    }
    if (value >= 0xc0) {
      output.push(0x20, value ^ 0x80)
      continue
    }
    if (i + 1 >= input.length) {
      break
    }
    i += 1
    const token = (value << 8) | input[i]
    const distance = (token >> 3) & 0x07ff
    const length = (token & 0x0007) + 3
    if (!distance || distance > output.length) {
      continue
    }
    for (let j = 0; j < length; j++) {
      output.push(output[output.length - distance])
    }
  }
  return new Uint8Array(output)
}

function createPageTextScanState() {
  return {
    explicitPageCount: 0,
    pageListCount: 0,
    pageBreakCount: 0,
    carry: ''
  }
}

function scanTextForPageNumber(state, text) {
  if (!state) {
    return
  }
  const source = state.carry + String(text || '')
  if (!state.explicitPageCount) {
    state.explicitPageCount = extractExplicitPageCount(source)
  }
  if (!state.pageListCount) {
    state.pageListCount = countPageListEntries(source)
  }
  state.pageBreakCount += countPageBreakTags(source, state.carry.length)
  state.carry = source.slice(-4096)
}

function getScannedTextPageNumber(state) {
  if (!state) {
    return UNKNOWN_PAGE_NUMBER
  }
  return state.explicitPageCount || state.pageListCount || state.pageBreakCount || UNKNOWN_PAGE_NUMBER
}

function countBinaryTextPages(bytes) {
  const chunkSize = 256 * 1024
  const state = createPageTextScanState()
  for (let start = 0; start < bytes.length; start += chunkSize) {
    const chunk = bytes.subarray(start, Math.min(start + chunkSize, bytes.length))
    scanTextForPageNumber(state, decodeBytesToString(chunk))
  }
  return getScannedTextPageNumber(state)
}

function countMobiTextRecordPages(bytes, records, compression, textRecordCount) {
  const state = createPageTextScanState()
  const limit = Math.min(textRecordCount, records.length - 1)
  for (let i = 1; i <= limit; i++) {
    const rawRecord = bytes.subarray(records[i].start, records[i].end)
    const content = compression == 2 ? decompressPalmDocRecord(rawRecord) : rawRecord
    scanTextForPageNumber(state, decodeBytesToString(content))
  }
  return getScannedTextPageNumber(state)
}

function countMobiPagesFromBuffer(buffer) {
  const bytes = new Uint8Array(buffer)
  const records = getMobiRecordRanges(bytes)
  if (records.length == 0) {
    return countBinaryTextPages(bytes)
  }

  const firstRecordStart = records[0].start
  const compression = readUInt16BE(bytes, firstRecordStart)
  const textRecordCount = readUInt16BE(bytes, firstRecordStart + 8)
  if (compression != 1 && compression != 2) {
    return countBinaryTextPages(bytes)
  }

  return countMobiTextRecordPages(bytes, records, compression, textRecordCount) || countBinaryTextPages(bytes)
}

async function countPdfPages(file) {
  const buffer = await readFileAsArrayBuffer(file)
  const bytes = new Uint8Array(buffer)
  let document = null
  try {
    const loadingTask = pdfjsLib.getDocument({
      data: bytes
    })
    document = await loadingTask.promise
    const count = normalizePageNumber(document && document.numPages)
    if (count > UNKNOWN_PAGE_NUMBER) {
      return count
    }
  } catch (error) {
    const fallbackBytes = bytes.length > 0 ? bytes : new Uint8Array(await readFileAsArrayBuffer(file))
    return countPdfPagesFromBytes(fallbackBytes)
  } finally {
    if (document && typeof document.destroy == 'function') {
      await document.destroy()
    }
  }
  return countPdfPagesFromBytes(bytes.length > 0 ? bytes : new Uint8Array(await readFileAsArrayBuffer(file)))
}

async function countEbookPages(file, extension) {
  const buffer = await readFileAsArrayBuffer(file)
  if (extension == 'epub') {
    return countEpubPagesFromBuffer(buffer)
  }
  return countMobiPagesFromBuffer(buffer)
}

export async function getCloudUploadPageNumber(file, fileName) {
  const extension = getFileExtension(fileName || (file && file.name))
  if (!extension || IMAGE_EXTENSIONS.includes(extension)) {
    return IMAGE_PAGE_NUMBER
  }
  try {
    if (extension == 'pdf') {
      return normalizePageNumber(await countPdfPages(file))
    }
    if (EBOOK_EXTENSIONS.includes(extension)) {
      return normalizePageNumber(await countEbookPages(file, extension))
    }
  } catch (error) {
    if (typeof console != 'undefined' && typeof console.warn == 'function') {
      console.warn('[cloudUpload] Failed to read pageNumber:', error)
    }
  }
  return EBOOK_EXTENSIONS.includes(extension) || extension == 'pdf'
    ? UNKNOWN_PAGE_NUMBER
    : IMAGE_PAGE_NUMBER
}
